import type { WidgetTemplate } from "./widget-template.js";
import {
  BranchVariantConfigSchema,
  FreezePickerConfigSchema,
  OptionSetConfigSchema,
  OptionTierMatrixConfigSchema,
  PerOptionDetailCardConfigSchema,
  RankedTierConfigSchema,
  ScalingMatrixConfigSchema,
  ShoppingListAggregatorConfigSchema,
  SizeAndYieldConfigSchema,
  StaticNoteConfigSchema,
  SummaryChecklistConfigSchema,
} from "./widget-templates/index.js";

// Per-template configSchema ownership (REVIEWS-3 LOW #1):
//
// | templateId                | configSchema owner phase    |
// |---------------------------|-----------------------------|
// | option-set                | Phase 6                     |
// | summary-checklist         | Phase 6                     |
// | static-note               | Phase 6 (NEW)               |
// | size-and-yield            | Phase 7 (Plan 05 wired)     |
// | ranked-tier               | Phase 7 (Plan 05 wired)     |
// | branch-variant            | Phase 7 (Plan 05 wired)     |
// | shopping-list-aggregator  | Phase 11 (Plan 02 wired)    |
// | scaling-matrix            | Phase 11 (NEW)              |
// | option-tier-matrix        | Phase 11 (NEW)              |
// | per-option-detail-card    | Phase 11 (NEW)              |
// | freeze-picker             | Phase 11.6 (NEW)            |
//
// Phase 11.6 Plan 02 registers freeze-picker (catalog union + entry + a typed
// FreezePickerConfigSchema); its render-side state REUSES the option-set {selected}
// shape (no new WidgetState variant). The renderer + the template-renderers Map
// entry are Plan 05 (Wave 2) — until then freeze-picker is cataloged-but-unregistered
// and renders the neutral UnregisteredTemplatePlaceholder (the established interim
// state; check-registry-closed.sh stays green — no .set/.delete/external-register).
//
// Phase 6 owns option-set + summary-checklist + static-note configSchema
// replacements (typed Zod schemas live under ./widget-templates/).
// Phase 7 owns size-and-yield + ranked-tier + branch-variant configSchema replacements
// (typed Zod schemas shipped by Plans 07-02/07-03/07-04; wired into the catalog by Plan 07-05).
// Phase 11 registers three additional templates (scaling-matrix, option-tier-matrix,
// per-option-detail-card) and swaps shopping-list-aggregator from its untyped
// placeholder to a typed configSchema. After Plan 02 every catalog entry carries a
// typed configSchema — no entry leaves its config root untyped. The catalog-test
// B-5 gate asserts the typed-placeholder list is empty as the regression guard.

export type TemplateId =
  | "option-set"
  | "summary-checklist"
  | "static-note"
  | "size-and-yield"
  | "ranked-tier"
  | "branch-variant"
  | "shopping-list-aggregator"
  | "scaling-matrix"
  | "option-tier-matrix"
  | "per-option-detail-card"
  | "freeze-picker";

// TemplateCatalogEntry<K> narrows the `id` field to the literal K while
// satisfying the WidgetTemplate structural constraint. This gives the catalog
// literal-type narrowing per REVIEWS Suggestion #12 under isolatedDeclarations
// (D-15): an explicit annotated type is required; `as const` is incompatible
// with z.ZodTypeAny property values under TS 5.8 isolatedDeclarations because
// the Zod return type is a non-literal complex interface type (TS9013).
//
// REVIEWS-3 HIGH-1: catalog declared as a plain object literal with
// `satisfies Record<TemplateId, WidgetTemplate>` — NOT the invalid form where
// Object.freeze wraps the literal before `as const`. That form is rejected by TS
// because `as const` requires a literal expression target, not a function-call result.
// The `satisfies` operator gives compile-time structural compliance AND key-exhaustiveness.
//
// isolatedDeclarations note (D-15): `as const satisfies Record<TemplateId, WidgetTemplate>`
// on an object with z.ZodTypeAny properties triggers TS9013 because `as const` requires
// all property values to be infer-able as literal types, but z.ZodTypeAny is a complex
// interface type. The fix: use an explicit `Record<TemplateId, TemplateCatalogEntry<TemplateId>>`
// annotation (inferrable) + `satisfies Record<TemplateId, WidgetTemplate>` (structural check).
// Literal id narrowing is preserved because TemplateCatalogEntry<K> types `id: K`.

type TemplateCatalogEntry<K extends TemplateId> = Omit<WidgetTemplate, "id"> & {
  readonly id: K;
};

export const TEMPLATE_CATALOG: {
  readonly "option-set": TemplateCatalogEntry<"option-set">;
  readonly "summary-checklist": TemplateCatalogEntry<"summary-checklist">;
  readonly "static-note": TemplateCatalogEntry<"static-note">;
  readonly "size-and-yield": TemplateCatalogEntry<"size-and-yield">;
  readonly "ranked-tier": TemplateCatalogEntry<"ranked-tier">;
  readonly "branch-variant": TemplateCatalogEntry<"branch-variant">;
  readonly "shopping-list-aggregator": TemplateCatalogEntry<"shopping-list-aggregator">;
  readonly "scaling-matrix": TemplateCatalogEntry<"scaling-matrix">;
  readonly "option-tier-matrix": TemplateCatalogEntry<"option-tier-matrix">;
  readonly "per-option-detail-card": TemplateCatalogEntry<"per-option-detail-card">;
  readonly "freeze-picker": TemplateCatalogEntry<"freeze-picker">;
} = {
  "option-set": {
    id: "option-set" as const,
    name: "Option Set",
    charter:
      "Multi-select picker over an option list with recipe-declared per-option optionCategories data; supports cardinality limits.",
    configSchema: OptionSetConfigSchema,
    supportedPlacements: ["options", "main"] as const,
  },
  "summary-checklist": {
    id: "summary-checklist" as const,
    name: "Summary Checklist",
    charter:
      "Ordered list of items with view modes (title / snippet / full / expanded) and group-by; supports per-item visibleWhen.",
    configSchema: SummaryChecklistConfigSchema,
    supportedPlacements: ["main"] as const,
  },
  "static-note": {
    id: "static-note" as const,
    name: "Static Note",
    charter:
      "Pure-display markdown blurb with tone (info / warning / note). No state, no source binding. Used for cooking-method disclaimers, ingredient-sourcing notes.",
    configSchema: StaticNoteConfigSchema,
    supportedPlacements: ["main"] as const,
  },
  "size-and-yield": {
    id: "size-and-yield" as const,
    name: "Size & Yield",
    charter:
      "Discrete-step size selector plus optional integer multiplier plus computed yield display.",
    configSchema: SizeAndYieldConfigSchema,
    supportedPlacements: ["main"] as const,
  },
  "ranked-tier": {
    id: "ranked-tier" as const,
    name: "Ranked Tier",
    charter:
      "Option-set view where each option carries an arbitrary-N-tier ranking with recipe-declared tier colors and labels.",
    configSchema: RankedTierConfigSchema,
    supportedPlacements: ["main"] as const,
  },
  "branch-variant": {
    id: "branch-variant" as const,
    name: "Branch Variant",
    charter:
      "Conditional sub-shape attached to specific options; carries variant label plus override fields.",
    configSchema: BranchVariantConfigSchema,
    supportedPlacements: ["main"] as const,
  },
  "shopping-list-aggregator": {
    id: "shopping-list-aggregator" as const,
    name: "Shopping List",
    charter:
      "Aggregates ingredients across selected options plus batch-set sizes into a unified shopping list.",
    configSchema: ShoppingListAggregatorConfigSchema,
    supportedPlacements: ["main"] as const,
  },
  "scaling-matrix": {
    id: "scaling-matrix" as const,
    name: "Scaling Matrix",
    charter:
      "Two discrete sizing axes plus a typeable/stepper multiplier with predefined series and computed per-pass yield.",
    configSchema: ScalingMatrixConfigSchema,
    supportedPlacements: ["main"] as const,
  },
  "option-tier-matrix": {
    id: "option-tier-matrix" as const,
    name: "Option Tier Matrix",
    charter: "Per-option clickable N-row selection matrix composed onto an option-set by id.",
    configSchema: OptionTierMatrixConfigSchema,
    supportedPlacements: ["main"] as const,
  },
  "per-option-detail-card": {
    id: "per-option-detail-card" as const,
    name: "Per-Option Detail Card",
    charter:
      "Composite per-selection detail card with recipe-declared category sections and optional per-item checkboxes.",
    configSchema: PerOptionDetailCardConfigSchema,
    supportedPlacements: ["main"] as const,
  },
  "freeze-picker": {
    id: "freeze-picker" as const,
    name: "Freeze Picker",
    charter:
      "Single-select picker over recipe-declared styles that drives a resolved select-implies-display payload.",
    configSchema: FreezePickerConfigSchema,
    supportedPlacements: ["main"] as const,
  },
} satisfies Record<TemplateId, WidgetTemplate>;

// Runtime immutability — applied separately because Object.freeze cannot be
// an as-const target (REVIEWS-3 HIGH-1 fix). The typed assertion above runs
// on the plain object literal; Object.freeze is a subsequent statement.
Object.freeze(TEMPLATE_CATALOG);
